/**
 * Geração de rótulos de breadcrumb a partir do `location.pathname`.
 *
 * Regras:
 *  - Remove a query string e o hash (defensivo — o React Router já entrega
 *    apenas o pathname, mas mantemos o `split('?')` para uso fora dele).
 *  - Oculta segmentos que são identificadores opacos: UUIDs (v1–v5),
 *    sequências hexadecimais longas (>= 16 chars), tokens base64-url e
 *    IDs puramente numéricos. Esses códigos poluem o breadcrumb sem
 *    agregar contexto ao usuário final.
 *  - Aplica `ROTULOS` para slugs conhecidos; demais segmentos são
 *    "humanizados" (hífens/underscores viram espaço + capitalização).
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_LONGO = /^[0-9a-f]{16,}$/i;
const NUMERICO = /^\d+$/;
const BASE64_URL = /^[A-Za-z0-9_-]{20,}$/;

const ROTULOS: Record<string, string> = {
  turmas: 'Turmas',
  disciplinas: 'Disciplinas',
  pea: 'Planos de Ensino e Aprendizagem',
  portal: 'Portal',
  professor: 'Professor',
  coordenacao: 'Coordenação',
  coordenador: 'Coordenação',
  aluno: 'Aluno',
  alunos: 'Alunos',
  laboratorios: 'Laboratórios',
  reprografia: 'Reprografia',
  equipamentos: 'Equipamentos',
  eventos: 'Eventos',
  cursos: 'Cursos',
  professores: 'Professores',
  unidades: 'Unidades',
  relatorios: 'Relatórios',
  parametros: 'Parâmetros',
  'parametros-academicos': 'Parâmetros Acadêmicos',
  'periodos-letivos': 'Períodos Letivos',
  dashboard: 'Dashboard',
  iam: 'IAM',
  ti: 'TI',
  rh: 'RH',
  setores: 'Setores',
  departamentos: 'Departamentos',
  calendario: 'Calendário',
  provas: 'Provas',
  impressao: 'Impressão',
  ativos: 'Ativos',
  reservas: 'Reservas',
  matrizes: 'Matrizes',
  censo: 'Censo',
  mec: 'MEC',
  ats: 'ATS',
  recrutamento: 'Recrutamento',
  candidato: 'Candidato',
  captacao: 'Captação',
  financeiro: 'Financeiro',
  configuracoes: 'Configurações',
  pessoas: 'Pessoas',
  onboarding: 'Onboarding',
  notificacoes: 'Notificações',
  bibliotecas: 'Bibliotecas',
  horarios: 'Horários',
  perfil: 'Perfil',
  'planos-ensino': 'Planos de Ensino e Aprendizagem',
  meus: 'Meus',
  minhas: 'Minhas',
};

export function ehIdOpaco(segmento: string): boolean {
  return (
    UUID_REGEX.test(segmento) ||
    HEX_LONGO.test(segmento) ||
    NUMERICO.test(segmento) ||
    BASE64_URL.test(segmento)
  );
}

function humanizar(seg: string): string {
  const decodificado = (() => {
    try {
      return decodeURIComponent(seg);
    } catch {
      return seg;
    }
  })();
  const slug = decodificado.toLowerCase();
  if (ROTULOS[slug]) return ROTULOS[slug];
  return decodificado
    .replace(/[-_]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export interface BreadcrumbItem {
  /** Chave estável (índice do segmento original — sobrevive a duplicatas). */
  key: string;
  /** Texto exibido. */
  label: string;
  /** Caminho acumulado até este segmento, usado como link no breadcrumb. */
  path: string;
}

/**
 * Converte um pathname em itens de breadcrumb prontos para render.
 * Aceita pathnames completos (incluindo `?query` ou `#hash`) e descarta
 * automaticamente segmentos vazios e identificadores opacos. Cada item
 * recebe o caminho acumulado até o seu segmento, permitir navegação.
 */
export function rotulosBreadcrumb(pathname: string, search = ''): BreadcrumbItem[] {
  if (!pathname) return [];
  const semQuery = pathname.split('?')[0].split('#')[0];
  const segmentos = semQuery.split('/');
  const itens: BreadcrumbItem[] = [];
  let caminhoAcumulado = '';
  segmentos.forEach((seg, idx) => {
    if (!seg) return;
    caminhoAcumulado += `/${seg}`;
    const ehConhecido = ROTULOS[seg.toLowerCase()] !== undefined;
    if (!ehConhecido && ehIdOpaco(seg)) return;
    itens.push({ key: `${idx}-${seg}`, label: humanizar(seg), path: caminhoAcumulado });
  });

  // Tabs de Parâmetros Acadêmicos: reflete a aba ativa no breadcrumb.
  if (semQuery === '/parametros-academicos') {
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    const tab = params.get('tab');
    if (tab === 'planos-ensino') {
      itens.push({
        key: 'tab-planos-ensino',
        label: 'Planos de Ensino e Aprendizagem',
        path: `${caminhoAcumulado}?tab=planos-ensino`,
      });
    }
  }

  return itens;
}
